<template>
    <div class="integrated-list">
        <div>
            <h4 v-if="title" class="list-title text-sm font-medium text-n-slate-12">{{ title }}</h4>
        </div>

        <!-- Input area -->
        <div class="input-container flex gap-2">
            <div class="relative flex-1">
                <input 
                    type="text" 
                    v-model="newItemText" 
                    :placeholder="inputPlaceholder" 
                    class="list-input w-full"
                    :class="{ 'error': isExceedingLimit, 'with-counter': newItemText.length > 0 }"
                    maxlength="32"
                    @keydown.enter.prevent="addItem" 
                />
                <div v-if="newItemText.length > 0" class="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div class="character-count" :class="{ 'limit-exceeded': isExceedingLimit }">
                        {{ newItemText.length }}/32
                    </div>
                </div>
            </div>
            <button 
                v-if="newItemText.length > 0"
                type="button"
                @click="addItem"
                class="flex items-center justify-center px-3 bg-n-brand text-white border-0 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 hover:brightness-110 active:scale-95 flex-shrink-0"
                style="height: 40px;"
                title="Adicionar categoria"
            >
                ↵
            </button>
        </div>

        <!-- Item list -->
        <div v-if="modelValue.length > 0" class="items-container">
            <div 
                v-for="(item, index) in modelValue" 
                :key="index" 
                class="list-item"
                @click="removeItem(index)"
            >
                <span class="item-name">{{ item.name }}</span>
                <span class="btn-remove-item">&times;</span>
            </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="emptyMessage" class="empty-container">
            <p class="empty-message">{{ emptyMessage }}</p>
        </div>
    </div>
</template>

<script>
import { ref, computed } from 'vue';

export default {
    name: 'IntegratedListView',
    props: {
        modelValue: {
            type: Array,
            required: true
        },
        title: {
            type: String,
            default: ''
        },
        inputPlaceholder: {
            type: String,
            default: 'Digite e pressione Enter para adicionar'
        },
        emptyMessage: {
            type: String,
            default: 'Nenhum item adicionado'
        }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const MAX_LENGTH = 32;
        const newItemText = ref('');
        
        const isExceedingLimit = computed(() => {
            return newItemText.value.length > MAX_LENGTH;
        });

        const addItem = () => {
            if (!newItemText.value.trim()) return;
            
            // Ensure text doesn't exceed the character limit
            const trimmedText = newItemText.value.trim().substring(0, MAX_LENGTH);

            const newItem = {
                id: Date.now().toString(),
                name: trimmedText
            };

            emit('update:modelValue', [...props.modelValue, newItem]);
            newItemText.value = '';
        };

        const removeItem = (index) => {
            const updatedItems = [...props.modelValue];
            updatedItems.splice(index, 1);
            emit('update:modelValue', updatedItems);
        };

        return {
            newItemText,
            isExceedingLimit,
            addItem,
            removeItem
        };
    }
}
</script>

<style scoped>
.integrated-list {
    margin-bottom: 20px;
}

.list-title {
    font-size: 14px;
    font-weight: 500;
    margin: 0 0 6px 0;
}

.input-container {
    /* Flexbox styling now handled by Tailwind classes */
}

.list-input {
    padding: 10px 12px;
    border: 1px solid rgb(var(--slate-4) / 1);
    border-radius: 8px;
    font-size: 14px;
    background-color: white;
    color: rgb(var(--slate-12) / 1);
    height: 40px;
    padding-right: 50px; /* Space for character counter */
}

.list-input.with-counter {
    padding-right: 60px; /* More space for counter */
}

.list-input.error {
    border-color: rgb(var(--ruby-9) / 1);
}

.dark .list-input {
    border-color: rgb(var(--slate-7) / 1);
    background-color: rgb(var(--slate-2) / 1);
    color: rgb(var(--slate-12) / 1);
}

.character-count {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: rgb(var(--slate-9) / 1);
}

.limit-exceeded {
    color: rgb(var(--ruby-9) / 1);
}

.items-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.list-item {
    display: flex;
    align-items: center;
    background-color: rgb(var(--slate-4) / 0.5);
    border-radius: 16px;
    padding: 4px 10px 4px 12px;
    cursor: pointer;
    transition: background-color 0.2s;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}

.list-item:hover {
    background-color: rgb(var(--slate-5) / 0.7);
}

.dark .list-item {
    background-color: rgb(var(--slate-7) / 0.5);
}

.dark .list-item:hover {
    background-color: rgb(var(--slate-8) / 0.7);
}

.item-name {
    font-size: 14px;
    color: rgb(var(--slate-12) / 1);
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.btn-remove-item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-left: 6px;
    color: rgb(var(--slate-10) / 1);
    font-size: 16px;
    padding: 0;
    transition: color 0.2s ease;
}

.list-item:hover .btn-remove-item {
    color: rgb(var(--slate-12) / 1);
}

.dark .btn-remove-item {
    color: rgb(var(--slate-9) / 1);
}

.dark .list-item:hover .btn-remove-item {
    color: rgb(var(--slate-11) / 1);
}

.empty-container {
    padding: 12px;
    background-color: rgb(var(--slate-3) / 0.5);
    border-radius: 8px;
    text-align: center;
}

.dark .empty-container {
    background-color: rgb(var(--slate-7) / 0.3);
}

.empty-message {
    color: rgb(var(--slate-10) / 1);
    font-size: 14px;
    margin: 0;
}

.dark .empty-message {
    color: rgb(var(--slate-9) / 1);
}
</style>